/**
 * 核心 Collector 类
 */

import type { EventInfo, BatchReportData, ReportFunction, HeaderInfo, UserInfo } from '../@types'
import type { EventTrackerInitOptions } from '../@types'
import { ConfigManager } from './config'
import { QueueManager } from './queue'
import { Token } from './token'
import { isFunction, isBrowser } from './utils'

/**
 * 插件接口
 */
export interface Plugin {
  name: string
  version?: string
  install: (collector: Collector) => void
  destroy?: () => void
}

/**
 * 插件构造函数
 */
export type PluginConstructor = new (...args: unknown[]) => Plugin

/**
 * Collector 核心类
 */
export class Collector {
  public readonly name: string
  public readonly config: ConfigManager
  public readonly queue: QueueManager<EventInfo>
  public readonly token: Token

  private plugins: Map<string, Plugin> = new Map()
  private static registeredPlugins: Map<string, PluginConstructor> = new Map()
  private initialized: boolean = false
  private started: boolean = false
  private enabled: boolean = true
  private isBatching: boolean = false
  private presetHeader: HeaderInfo = {
    platform: 'web',
    os_name: 'Unknown',
    browser: 'Unknown',
  }

  constructor(name: string = 'default') {
    this.name = name
    this.config = new ConfigManager()
    this.queue = new QueueManager<EventInfo>()
    this.token = new Token()
  }

  /**
   * 注册插件类
   */
  static usePlugin(pluginConstructor: PluginConstructor, name: string): void {
    if (Collector.registeredPlugins.has(name)) {
      console.warn(`[Event Tracker] 插件 ${name} 已注册`)
      return
    }
    Collector.registeredPlugins.set(name, pluginConstructor)
  }

  /**
   * 初始化
   */
  init(options: EventTrackerInitOptions): void {
    if (this.initialized) {
      this.config.warn('Collector 已初始化')
      return
    }

    if (!isBrowser()) {
      this.config.warn('不在浏览器环境')
      return
    }

    if (!isFunction(options.reportLogs)) {
      this.config.warn('reportLogs 未配置')
      return
    }

    this.config.init(options)
    this.initToken()
    this.initPresetHeader()
    this.installRegisteredPlugins()
    this.bindPageUnload()
    this.initialized = true
    this.config.debug('Collector 初始化完成')
  }

  /**
   * 动态配置（初始化后可调用）
   */
  setConfig(options: Partial<EventTrackerInitOptions>): void {
    if (!this.initialized) {
      this.config.warn('请先调用 init() 初始化')
      return
    }

    // 更新配置
    Object.keys(options).forEach((key) => {
      const k = key as keyof EventTrackerInitOptions
      if (options[k] !== undefined) {
        this.config.set(k, options[k] as EventTrackerInitOptions[typeof k])
      }
    })

    this.config.debug('配置已更新', options)
  }

  /**
   * 启动 SDK（开始上报）
   */
  start(): void {
    if (!this.initialized) {
      this.config.warn('请先调用 init() 初始化')
      return
    }

    this.started = true
    this.enabled = true
    this.config.debug('SDK 已启动')
  }

  /**
   * 停止 SDK（停止上报）
   */
  stop(): void {
    this.started = false
    this.enabled = false
    this.config.debug('SDK 已停止')
  }

  /**
   * 初始化 Token（身份标识）
   * Token 在构造时已自动生成 anonymous_id，这里只做日志输出
   */
  private initToken(): void {
    this.config.debug('Token 已初始化', {
      anonymous_id: this.token.getAnonymousId(),
    })
  }

  /**
   * 初始化预置 Header（缓存）
   */
  private initPresetHeader(): void {
    const clientInfo = this.config.getClientInfo()
    const { timezone, tz_offset } = this.config.getTimezone()
    const globalParams = this.config.get('globalParams') || {}

    // 基础必填字段
    this.presetHeader = {
      platform: (clientInfo?.platform || 'web') as 'web' | 'wap' | 'ios' | 'android',
      os_name: clientInfo?.os_name || 'Unknown',
      browser: clientInfo?.browser || 'Unknown',
      sdk_version: '1.0.0',
      sdk_lib: 'js',
    }

    // 只添加有值的可选字段
    if (clientInfo?.os_version) this.presetHeader.os_version = clientInfo.os_version
    if (clientInfo?.device_model) this.presetHeader.device_model = clientInfo.device_model
    if (clientInfo?.browser_version) this.presetHeader.browser_version = clientInfo.browser_version
    if (clientInfo?.screen_width) {
      this.presetHeader.screen_width = clientInfo.screen_width
      this.presetHeader.width = clientInfo.screen_width
    }
    if (clientInfo?.screen_height) {
      this.presetHeader.screen_height = clientInfo.screen_height
      this.presetHeader.height = clientInfo.screen_height
    }
    if (clientInfo?.screen_width && clientInfo?.screen_height) {
      this.presetHeader.resolution = `${clientInfo.screen_width}x${clientInfo.screen_height}`
    }
    if (clientInfo?.language) this.presetHeader.language = clientInfo.language
    if (timezone !== undefined) this.presetHeader.timezone = timezone
    if (tz_offset !== undefined) this.presetHeader.tz_offset = tz_offset
    if (clientInfo?.referrer) this.presetHeader.referrer = clientInfo.referrer
    if (clientInfo?.referrer_host) this.presetHeader.referrer_host = clientInfo.referrer_host
    if (clientInfo?.utm) this.presetHeader.utm = clientInfo.utm

    // 合并全局参数（过滤掉 undefined 值）
    Object.keys(globalParams).forEach((key) => {
      const value = globalParams[key]
      if (value !== undefined) {
        this.presetHeader[key] = value
      }
    })

    this.config.debug('预置 Header 已初始化', this.presetHeader)
  }

  /**
   * 安装已注册的插件
   */
  private installRegisteredPlugins(): void {
    Collector.registeredPlugins.forEach((PluginClass, name) => {
      try {
        const plugin = new PluginClass()
        plugin.install(this)
        this.plugins.set(name, plugin)
        this.config.debug(`插件 ${name} 已安装`)
      } catch (error) {
        this.config.error(`插件 ${name} 安装失败`, error)
      }
    })
  }

  /**
   * 获取插件
   */
  getPlugin<T extends Plugin>(name: string): T | undefined {
    return this.plugins.get(name) as T
  }

  /**
   * 上报事件（兼容 track 方法名）
   */
  track(event: string, params?: Record<string, unknown>): void {
    this.event(event, params)
  }

  /**
   * 上报事件（主方法，走队列 + XHR）
   */
  event(event: string, params?: Record<string, unknown>): void {
    if (!this.isEnabled() || !this.started) return

    // 构建事件基础信息
    let eventInfo = this.buildEventInfo(event, params)

    // 调用 onBeforeTrack 钩子
    const onBeforeTrack = this.config.get('onBeforeTrack')
    if (isFunction(onBeforeTrack)) {
      try {
        const result = onBeforeTrack(eventInfo)
        if (result === false) {
          this.config.debug('onBeforeTrack 返回 false，取消上报')
          return
        }
        if (result) {
          eventInfo = result
        }
      } catch (error) {
        this.config.error('onBeforeTrack 执行失败', error)
        this.config.callErrorHook(error as Error, { event: eventInfo })
      }
    }

    // 入队
    this.queue.push(eventInfo, (log: EventInfo) => {
      this.config.debug('记录事件', JSON.stringify(log))
      this.reportAllLogs()
    })
  }

  /**
   * 使用 sendBeacon 立即上报（用于页面关闭等场景）
   * 不走队列，直接上报
   */
  beaconEvent(event: string, params?: Record<string, unknown>): void {
    if (!this.isEnabled() || !this.started) return

    // 构建事件基础信息
    let eventInfo = this.buildEventInfo(event, params)

    // 调用 onBeforeTrack 钩子
    const onBeforeTrack = this.config.get('onBeforeTrack')
    if (isFunction(onBeforeTrack)) {
      try {
        const result = onBeforeTrack(eventInfo)
        if (result === false) {
          this.config.debug('onBeforeTrack 返回 false，取消上报')
          return
        }
        if (result) {
          eventInfo = result
        }
      } catch (error) {
        this.config.error('onBeforeTrack 执行失败', error)
        this.config.callErrorHook(error as Error, { event: eventInfo })
      }
    }

    // 直接构建数据并用 sendBeacon 上报（不走队列）
    try {
      const reportData = this.buildBatchReportData([eventInfo])
      const reportLogsBeacon = this.config.get('reportLogsBeacon') as
        | ((data: BatchReportData) => boolean)
        | undefined

      if (isFunction(reportLogsBeacon)) {
        const success = reportLogsBeacon(reportData)
        if (success) {
          this.config.debug(`beaconEvent 上报成功: ${event}`)
        } else {
          this.config.warn(`beaconEvent 上报失败: ${event}`)
        }
      } else {
        this.config.warn('reportLogsBeacon 未配置，无法使用 beaconEvent')
      }
    } catch (error) {
      this.config.error(`beaconEvent 上报异常: ${event}`, error)
      this.config.callErrorHook(error as Error, { event: eventInfo })
    }
  }

  /**
   * 构建事件基础信息
   */
  private buildEventInfo(event: string, params?: Record<string, unknown>): EventInfo {
    const sessionPlugin = this.getPlugin('session')
    const sessionId = sessionPlugin
      ? (sessionPlugin as unknown as { getSessionId: () => string }).getSessionId()
      : ''

    return {
      event,
      ...(params && Object.keys(params).length > 0 ? { params } : {}),
      local_time_ms: Date.now(),
      session_id: sessionId,
    }
  }

  /**
   * 构建批量上报数据
   */
  private buildBatchReportData(events: EventInfo[]): BatchReportData {
    // 获取业务方提供的用户信息
    const getUserInfo = this.config.get('getUserInfo')
    const userInfo: UserInfo | undefined = isFunction(getUserInfo) ? getUserInfo() : undefined

    // 构建最终的 user 对象
    // - 业务方数据优先（透传）
    // - SDK 补充 anonymous_id
    // - user_unique_id 优先用业务方的，否则用 anonymous_id 兜底
    const anonymous_id = this.token.getAnonymousId()
    const user: UserInfo = {
      ...userInfo,
      anonymous_id,
      user_unique_id: userInfo?.user_unique_id || anonymous_id,
    }

    return {
      header: this.presetHeader,
      events,
      user,
      local_time: Math.floor(Date.now() / 1000),
    }
  }

  /**
   * 批量上报
   */
  private async reportAllLogs(): Promise<void> {
    if (this.queue.length === 0 || this.isBatching) return

    this.isBatching = true
    const batchSize = this.config.get('batchSize') || 10
    const events = this.queue.shift(batchSize)

    this.config.debug(`上报 ${events.length} 条，队列剩余 ${this.queue.length} 条`)

    try {
      const reportData = this.buildBatchReportData(events)
      const reportLogs = this.config.get('reportLogs') as ReportFunction
      await reportLogs(reportData)
      this.config.debug(`上报成功 ${events.length} 条`)
    } catch (error) {
      this.config.error(`上报失败，已丢弃 ${events.length} 条`, error)
      this.config.callErrorHook(error as Error, { events })
    } finally {
      this.isBatching = false
      if (this.queue.length > 0) {
        this.reportAllLogs()
      }
    }
  }

  /**
   * 刷新队列中的事件
   * @param useBeacon 是否使用 sendBeacon（默认 true，用于页面卸载场景）
   */
  flush(useBeacon: boolean = true): void {
    const length = this.queue.length
    if (length === 0 || this.isBatching) return

    this.isBatching = true
    const events = this.queue.shift(length)
    this.config.debug(`flush 刷新队列，上报 ${events.length} 条，useBeacon=${useBeacon}`)

    try {
      const reportData = this.buildBatchReportData(events)

      if (useBeacon) {
        // 使用 sendBeacon 上报（页面卸载场景）
        const reportLogsBeacon = this.config.get('reportLogsBeacon') as
          | ((data: BatchReportData) => boolean)
          | undefined
        if (isFunction(reportLogsBeacon)) {
          const success = reportLogsBeacon(reportData)
          if (success) {
            this.config.debug(`sendBeacon 上报成功 ${events.length} 条`)
          } else {
            this.config.warn(`sendBeacon 上报失败，尝试使用常规方式`)
            // sendBeacon 失败时尝试常规上报（不保证成功）
            const reportLogs = this.config.get('reportLogs') as ReportFunction
            if (isFunction(reportLogs)) {
              reportLogs(reportData).catch((error: Error) => {
                this.config.warn('常规上报失败', error)
              })
            }
          }
        } else {
          // 如果没有配置 reportLogsBeacon，降级使用常规上报
          this.config.warn('reportLogsBeacon 未配置，降级使用常规上报')
          const reportLogs = this.config.get('reportLogs') as ReportFunction
          if (isFunction(reportLogs)) {
            reportLogs(reportData).catch((error: Error) => {
              this.config.warn('上报失败', error)
            })
          }
        }
      } else {
        // 使用常规上报
        const reportLogs = this.config.get('reportLogs') as ReportFunction
        if (isFunction(reportLogs)) {
          reportLogs(reportData).catch((error: Error) => {
            this.config.warn('上报失败', error)
          })
        }
      }
    } catch (error) {
      this.config.warn('flush 上报失败', error)
    }

    this.isBatching = false
  }

  /**
   * 绑定页面卸载事件
   */
  private bindPageUnload(): void {
    if (!isBrowser()) return

    window.addEventListener('beforeunload', () => {
      this.flush(true)
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.queue.length > 0) {
        this.flush(true)
      }
    })
  }

  /**
   * 启用/禁用
   */
  enable(): void {
    this.enabled = true
    this.config.debug('SDK 已启用')
  }

  disable(): void {
    this.enabled = false
    this.queue.clear()
    this.config.debug('SDK 已禁用')
  }

  isEnabled(): boolean {
    const enableConfig = this.config.get('enable')
    return this.enabled && (enableConfig ?? true)
  }

  /**
   * 销毁
   */
  destroy(): void {
    this.flush(true)
    this.plugins.forEach((plugin) => {
      if (plugin.destroy) {
        try {
          plugin.destroy()
          this.config.debug(`插件 ${plugin.name} 已销毁`)
        } catch (error) {
          this.config.error(`插件 ${plugin.name} 销毁失败`, error)
        }
      }
    })
    this.plugins.clear()
    this.initialized = false
    this.config.debug('Collector 已销毁')
  }
}
